import React, {useEffect, useMemo, useRef, useState} from 'react';
import {defineMessages} from 'react-intl';
import {useNavigate} from 'react-router';
import {defineAccessControlledResource, PoliciesAccessRule, Policy} from '@igp/shared/access-control';
import type {FilterWithPlacement} from '@igp/shared/components/filter';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import type {TabData} from '@igp/shared/components/tabs';
import {RoutingTabs} from '@igp/shared/components/tabs';
import {EntityType} from '@models';

import {withAccessCheck} from '@access-control';
import {routes} from '@config';
import {
    CountryLevelPartnerReportDataGrid,
    CountryLevelPartnerReportDownloadCsvButton,
    CountryLevelPartnerReportQueryFilters,
    useCountryLevelPartnerReport,
    useCountryLevelPartnerReportSettings,
} from '@features/country-level-partner-report';
import type {CountryLevelPartnerReportViewModelKeys} from '@features/country-level-partner-report/types';

const localized = defineMessages({
    title: {
        id: 'countryLevelPartnerReport_Title',
        defaultMessage: 'Country Level Partner Report',
    },
    gameRevenue: {
        id: 'countryLevelPartnerReport_GameRevenueOverview',
        defaultMessage: 'Game Revenue Overview',
    },
    userActivity: {
        id: 'countryLevelPartnerReport_UserActivityOverview',
        defaultMessage: 'User Activity Overview',
    },
    depositActivity: {
        id: 'countryLevelPartnerReport_DepositActivityOverview',
        defaultMessage: 'Deposit Activity Overview',
    },
});

type TabId = 'GameRevenueOverview' | 'UserActivityOverview' | 'DepositActivityOverview';

type CountryLevelPartnerReportProps = {
    tabId: TabId;
};

function CountryLevelPartnerReportBaseInner({tabId}: CountryLevelPartnerReportProps) {
    const navigate = useNavigate();

    const tabs: TabData[] = useMemo(
        () =>
            [
                {value: routes.countryLevelPartnerReportGameRevenue, title: localized.gameRevenue},
                {value: routes.countryLevelPartnerReportUserActivity, title: localized.userActivity},
                {value: routes.countryLevelPartnerReportDepositActivity, title: localized.depositActivity},
            ].filter(t => t),
        []
    );

    const tabsFields: Record<TabId, CountryLevelPartnerReportViewModelKeys[]> = {
        GameRevenueOverview: [
            'date_ts',
            'country',
            'poker_cash_ggr',
            'poker_mtt_ggr',
            'global_spins_ggr',
            'casino_ggr',
            'sports_ggr',
            'total_ggr',
        ],
        UserActivityOverview: [
            'date_ts',
            'country',
            'new_users',
            'daily_active_users',
            'total_hands',
            'total_mtt_entries',
            'total_bet_amount',
            'payout_amount',
        ],
        DepositActivityOverview: [
            'date_ts',
            'country',
            'first_time_deposit_users',
            'first_deposit_amount',
            'retained_deposit_users',
            'retained_deposit_amount',
            'deposit_users',
            'deposit_count',
            'total_deposit_amount',
            'withdrawal_users',
            'withdrawal_count',
            'total_withdrawal_amount',
            'net_deposit',
            'promotional_spend',
        ],
    };
    const allFields: CountryLevelPartnerReportViewModelKeys[] = [...new Set(Object.values(tabsFields).flat())];

    const pinnedColumns: CountryLevelPartnerReportViewModelKeys[] = ['date_ts'];
    const columns = tabsFields[tabId];

    const {
        items,
        rawItems,
        summary,
        summaryRaw,
        totalCount,
        searchFilter,
        filterString,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
    } = useCountryLevelPartnerReport({
        viewType: 'CountryLevelPartnerReport',
        fields: allFields,
        defaultPaging: {page: 1, pageSize: 25},
        defaultSorting: [{field: 'date_ts', sort: 'desc'}],
        validateFilter: () => true,
        hasSummary: true,
        syncWithUrl: true,
    });
    const {visibleColumns, onVisibleColumnsChange} = useCountryLevelPartnerReportSettings({
        gridId: tabId,
        defaultColumns: columns,
        pinnedColumns,
    });

    const [primaryNode, setPrimaryNode] = useState<Omit<FilterWithPlacement, 'filterName'>>();
    const [secondaryNode, setSecondaryNode] = useState<Omit<FilterWithPlacement, 'filterName'>>();

    const primaryRef = useRef<HTMLDivElement>(undefined);
    const secondaryRef = useRef<HTMLDivElement>(undefined);

    useEffect(() => {
        if (primaryRef.current) {
            setPrimaryNode({nodeId: 'primary', node: primaryRef.current});
        }
        if (secondaryRef.current) {
            setSecondaryNode({nodeId: 'secondary', node: secondaryRef.current});
        }
    }, []);

    function handleTabOpen(path: string) {
        navigate(path);
    }

    return (
        <LayoutQueue
            header={
                <>
                    <PageHeader
                        header={localized.title}
                        actions={
                            <CountryLevelPartnerReportDownloadCsvButton
                                mode="server"
                                hasSummary
                                keys={visibleColumns}
                                filter={filterString}
                                filename={localized.title}
                                total={totalCount}
                                disabled={!totalCount}
                            />
                        }
                        removeBottomSpacing
                    />
                    <RoutingTabs tabs={tabs} onOpenTab={handleTabOpen} />
                </>
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems ref={primaryRef} />
                        <ToolbarItems secondary ref={secondaryRef} />
                    </ToolbarItemsContainer>
                    <CountryLevelPartnerReportQueryFilters
                        model={filterString}
                        onChange={handleFilterChange}
                        availableFilters={[
                            {filterName: 'countries', ...primaryNode},
                            {filterName: 'date', ...secondaryNode},
                        ]}
                        countryEntity={EntityType.Country}
                    />
                </Toolbar>
            }
            body={
                <CountryLevelPartnerReportDataGrid
                    configurableColumns={{pinnedColumns, visibleColumns, onVisibleColumnsChange}}
                    columns={columns}
                    rows={items}
                    rawModels={rawItems}
                    pinnedRow={items?.length > 0 ? summary : null}
                    pinnedRawModel={summaryRaw}
                    paging={{
                        page: searchFilter?.paging?.page,
                        pageSize: searchFilter?.paging?.pageSize,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={{sortModel: searchFilter?.sorting}}
                    onSortChange={handleSortChange}
                />
            }
        />
    );
}

export const countryLevelPartnerReportResource = defineAccessControlledResource({
    name: 'CountryLevelPartnerReport',
    rules: new PoliciesAccessRule([new Policy('admin'), new Policy('agent-manager')]),
});

export const CountryLevelPartnerReportBase = withAccessCheck(CountryLevelPartnerReportBaseInner, countryLevelPartnerReportResource);
